import { useState } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import { Download, FileImage, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { API } from "../constants";

const COPY = {
  ko: {
    csv: "CSV",
    xlsx: "Excel",
    pdf: "PDF",
    png: "PNG",
    generating: "생성 중...",
    exportTitle: "내보내기",
    exportDesc: "선택 그룹 기준으로 파일을 생성합니다. CSV/Excel/PDF에는 AI 리포트가 포함됩니다.",
    success: "파일 다운로드가 시작되었습니다",
    failed: "내보내기에 실패했습니다",
  },
  en: {
    csv: "CSV",
    xlsx: "Excel",
    pdf: "PDF",
    png: "PNG",
    generating: "Generating...",
    exportTitle: "Export",
    exportDesc: "Files are generated for the selected group. CSV/Excel/PDF include the AI report.",
    success: "Download started",
    failed: "Export failed",
  },
  zh: {
    csv: "CSV",
    xlsx: "Excel",
    pdf: "PDF",
    png: "PNG",
    generating: "生成中...",
    exportTitle: "导出",
    exportDesc: "为所选分组生成文件。CSV/Excel/PDF 包含 AI 报告。",
    success: "下载已开始",
    failed: "导出失败",
  },
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const parseFilename = (disposition, fallback) => {
  const match = disposition?.match(/filename=([^;]+)/i);
  return match ? match[1].replaceAll('"', "") : fallback;
};

export const SpilloverExportCenter = ({
  lang = "ko",
  headers,
  selectedGroup,
  selectedGroupName,
  days,
  activeTab,
  setActiveTab,
  networkSnapshotRef,
}) => {
  const copy = COPY[lang] || COPY.ko;
  const [loadingType, setLoadingType] = useState("");

  const exportFile = async (type) => {
    if (!selectedGroup) return;
    setLoadingType(type);
    try {
      const response = await axios.get(`${API}/spillover/export/${type}`, {
        headers,
        params: { group_id: selectedGroup, days, language: lang, include_ai_report: true },
        responseType: "blob",
      });
      const fallback = `spillover_${selectedGroupName || "group"}_${days}d.${type === "xlsx" ? "xlsx" : type}`;
      downloadBlob(response.data, parseFilename(response.headers["content-disposition"], fallback));
      toast.success(copy.success);
    } catch {
      toast.error(copy.failed);
    } finally {
      setLoadingType("");
    }
  };

  const exportPng = async () => {
    if (!networkSnapshotRef?.current) return;
    const previousTab = activeTab;
    setLoadingType("png");
    try {
      setActiveTab("flow-network");
      await new Promise((resolve) => setTimeout(resolve, 900));
      const canvas = await html2canvas(networkSnapshotRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        windowWidth: 1400,
      });
      const dataUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `spillover_network_${selectedGroupName || "group"}_${days}d.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(copy.success);
    } catch {
      toast.error(copy.failed);
    } finally {
      setActiveTab(previousTab);
      setLoadingType("");
    }
  };

  const actions = [
    { key: "csv", label: copy.csv, icon: FileSpreadsheet, onClick: () => exportFile("csv") },
    { key: "xlsx", label: copy.xlsx, icon: FileSpreadsheet, onClick: () => exportFile("xlsx") },
    { key: "pdf", label: copy.pdf, icon: FileText, onClick: () => exportFile("pdf") },
    { key: "png", label: copy.png, icon: FileImage, onClick: exportPng },
  ];

  return (
    <Card data-testid="spillover-export-center-card" className="border-border/60">
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-foreground">{copy.exportTitle}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{copy.exportDesc}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {actions.map((action) => {
            const Icon = action.icon;
            const isLoading = loadingType === action.key;
            return (
              <Button
                key={action.key}
                type="button"
                variant="outline"
                size="sm"
                onClick={action.onClick}
                disabled={!selectedGroup || !!loadingType}
                className="gap-1.5"
                data-testid={`spillover-export-${action.key}-button`}
              >
                {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
                {isLoading ? copy.generating : action.label}
              </Button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
